import { IActiveCountry, IActiveGroup } from 'services/APIService';
import { useState, useCallback, useMemo, useEffect } from 'react';
import {
    map,
    find,
    filter,
    includes,
    toLower,
    get,
    size,
    flow,
    keyBy,
    trim,
    isEmpty,
    startsWith,
} from 'lodash/fp';
import {
    FGButton,
    FGDivider,
    FGIcons,
    FGInput,
    FGStack,
    FGStackItem,
    FGTile,
    FGTypography,
} from '@fg/design-system';
import NewCountryPickerGroupSection from './NewCountryPickerGroupSection';
import NewCountryPickerCountryListItem from './NewCountryPickerCountryListItem';
import NewCountryPickerSelectAllListItem from './NewCountryPickerSelectAllListItem';
import { sortCountriesByName } from 'utils/countryUtils';
import React from 'react';

export type Props = {
    isMultiSelect: boolean;
    countriesList: IActiveCountry[];
    selectedCountriesList: number[];
    groups?: IActiveGroup[];
    onGroupClicked?: (id: string) => void;
    onCountrySelected: (id: number) => void;
    onAllSelected?: (id) => void;
    onAllUnselected?: (id) => void;
    canSelectAllCountries: boolean;
    activeGroupScroll?: number;
    activeGroupId?: string;
};

const NewCountryPicker = (props: Props) => {
    const {
        isMultiSelect,
        countriesList,
        selectedCountriesList,
        groups,
        onCountrySelected,
        canSelectAllCountries,
    } = props;
    const [searchInput, setSearchInput] = useState<string>('');
    const [activeGroupId, setActiveGroupId] = useState<string>('');
    useEffect(() => {
        setActiveGroupId(props.activeGroupId);
    }, [props.activeGroupId]);
    const onGroupClicked = useCallback((id: string) => {
        setSearchInput('');
        setActiveGroupId(id);
    }, []);
    const activeCountryGroup = useMemo(
        () => find(group => group.id === activeGroupId, groups),
        [groups, activeGroupId]
    );
    const activeGroupCountries = useMemo(
        () =>
            filter(
                country =>
                    !activeCountryGroup ||
                    includes(country.id, get('countries', activeCountryGroup)),
                countriesList
            ),
        [countriesList, activeCountryGroup]
    );
    const selectedCountriesFromActiveGroup = useMemo(
        () => {
            const activeGroupCountriesIds = map(
                get('id'),
                activeGroupCountries
            );
            return filter(
                countryId =>
                    !activeGroupId ||
                    includes(countryId, activeGroupCountriesIds),
                selectedCountriesList
            );
        },
        [countriesList, activeCountryGroup, selectedCountriesList]
    );
    const isAllSelected = useMemo(() => {
        return size(activeGroupCountries) === size(selectedCountriesFromActiveGroup);
    }, [selectedCountriesList, activeGroupCountries]);
    const isSomeSelected = useMemo(
        () =>
            size(selectedCountriesFromActiveGroup) > 0 &&
            size(selectedCountriesFromActiveGroup) < size(activeGroupCountries),
        [selectedCountriesFromActiveGroup, activeGroupCountries]
    );
    const searchInputLower = trim(toLower(searchInput));
    const visibleCountries = useMemo((): IActiveCountry[] => {
        const countriesById: {
            [countryID: number]: IActiveCountry;
        } = keyBy((country: IActiveCountry) => country.id, countriesList);
        // When a group is active the list is rebuilt from `activeCountryGroup.countries`,
        // which is an array of country IDs in the group's stored (ID) order. Re-sort by
        // name so the picker is always alphabetically ordered, matching the top-level list.
        const countriesForActiveGroup = sortCountriesByName(
            activeCountryGroup
                ? flow(
                      get('countries'),
                      map((countryId: number) => get(countryId, countriesById)),
                      filter(Boolean)
                  )(activeCountryGroup)
                : countriesList
        );

        if (!searchInputLower) {
            return countriesForActiveGroup;
        }

        return filter(country => {
            const countryLower = toLower(get('name', country));
            if (size(searchInputLower) === 1) {
                return startsWith(searchInputLower, countryLower);
            }
            return includes(searchInputLower, countryLower);
        }, countriesForActiveGroup);
    }, [activeCountryGroup, searchInputLower, countriesList]);
    const onSearchInputChange = useCallback(
        event => setSearchInput(event.target.value),
        []
    );
    const onAllItemsClick = useCallback(() => {
        if (isAllSelected) {
            props.onAllUnselected?.(activeGroupId);
        } else {
            props.onAllSelected?.(activeGroupId);
        }
    }, [isAllSelected, props.onAllUnselected, props.onAllSelected, activeGroupId]);
    const isSearchActive = !isEmpty(searchInput);
    return (
        <FGStack
            style={{
                height: isSearchActive ? 'auto' : '100%',
            }}
            gap={0}>
            <FGTile
                padding="small"
                appearance="secondary"
                fullHeight={!isSearchActive}
                style={{
                    borderRadius: 3,
                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.2)',
                }}>
                <FGStack
                    direction="column"
                    gap={0}
                    style={{
                        height: isSearchActive ? 'auto' : '100%',
                        width: '100%',
                    }}>
                    {activeGroupId && (
                        <FGStackItem>
                            <FGStack gap={4} alignItems="center">
                                <FGButton
                                    aria-label="User account"
                                    onClick={e => {
                                        e.stopPropagation?.();
                                        setActiveGroupId(null);
                                    }}
                                    variant="ghost">
                                    <FGIcons icon="ArrowLeft" />
                                </FGButton>
                                <FGTypography
                                    tag="label"
                                    variant="label2"
                                    color="strong">
                                    {activeCountryGroup.name}
                                </FGTypography>
                            </FGStack>
                        </FGStackItem>
                    )}
                    <FGStackItem>
                        <FGTile padding="small" appearance="secondary">
                            <div
                                style={{
                                    width: '100%',
                                }}>
                                <FGInput
                                    autoComplete="off"
                                    autoFocus
                                    value={searchInput}
                                    onChange={onSearchInputChange}
                                    onKeyDown={e => e.stopPropagation()}
                                    size="l"
                                    leftElement={
                                        <FGIcons icon="Search" size={12} />
                                    }
                                    placeholder="Search country"
                                    id={'country-search-input'}
                                />
                            </div>
                        </FGTile>
                    </FGStackItem>
                    {isEmpty(searchInput) && isEmpty(activeGroupId) && (
                        <FGStackItem>
                            <NewCountryPickerGroupSection
                                groups={groups}
                                selectedGroup={activeGroupId}
                                onGroupClick={onGroupClicked}
                            />
                        </FGStackItem>
                    )}
                    <FGStackItem>
                        <FGDivider />
                    </FGStackItem>
                    {canSelectAllCountries && isEmpty(searchInput) && (
                        <>
                            <FGStackItem>
                                <NewCountryPickerSelectAllListItem
                                    onClick={onAllItemsClick}
                                    isAllSelected={isAllSelected}
                                    isSomeSelected={isSomeSelected}
                                    numCountriesSelected={size(
                                        selectedCountriesFromActiveGroup
                                    )}
                                />
                            </FGStackItem>
                            <FGStackItem>
                                <FGDivider />
                            </FGStackItem>
                        </>
                    )}
                    <FGStackItem
                        style={
                            isSearchActive
                                ? { paddingTop: 8 }
                                : {
                                      //todo: refactor
                                      flex: 1,
                                      height: 0,
                                      paddingTop: 8,
                                  }
                        }>
                        <div
                            style={{
                                height: isSearchActive ? 'auto' : '100%',
                                maxHeight: isSearchActive ? 320 : undefined,
                                overflowY: 'auto',
                            }}>
                            <FGStack direction="column" gap={4}>
                                {map((countryListItem: IActiveCountry) => {
                                    return (
                                        <NewCountryPickerCountryListItem
                                            key={countryListItem.id}
                                            isMultiSelect={isMultiSelect}
                                            id={countryListItem.id}
                                            name={countryListItem.name}
                                            onClick={() => {
                                                onCountrySelected(
                                                    countryListItem.id
                                                );
                                            }}
                                            isSelected={includes(
                                                countryListItem.id,
                                                selectedCountriesList
                                            )}
                                        />
                                    );
                                }, visibleCountries)}
                            </FGStack>
                        </div>
                    </FGStackItem>
                </FGStack>
            </FGTile>
        </FGStack>
    );
};

export default NewCountryPicker;

//todo: refactor with AbsoluteFill component
export const NewCountryPickerFill = React.memo(
    ({ children, top = 50 }: { children: React.ReactNode; top?: number }) => {
        return (
            <div
                style={{
                    position: 'absolute',
                    top,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999999,
                    pointerEvents: 'none',
                }}>
                <div
                    style={{
                        height: '100%',
                        pointerEvents: 'auto',
                    }}>
                    {children}
                </div>
            </div>
        );
    }
);
